// src/services/crm/relancesService.js
import api from '../api';

const BASE     = 'prospects/relances/';
const PROSPECT = (prospectId) => `prospects/${prospectId}/relances/`;

// ══════════════════════════════════════════════════════════════════════════════
//  MAPPING  statut_calcule (Django slug) → libellé + couleur CSS React
// ══════════════════════════════════════════════════════════════════════════════

export const STATUT_CONFIG = {
  en_retard:   { label: 'En retard',   badgeCls: 'badge-late',   cardCls: 'reminder-late',   color: '#ef4444' },
  aujourd_hui: { label: "Aujourd'hui", badgeCls: 'badge-today',  cardCls: 'reminder-today',  color: '#f59e0b' },
  a_venir:     { label: 'À venir',     badgeCls: 'badge-future', cardCls: 'reminder-future', color: '#10b981' },
  fait:        { label: 'Fait',        badgeCls: 'badge-done',   cardCls: 'reminder-done',   color: '#6b7280' },
};

// ══════════════════════════════════════════════════════════════════════════════
//  MAPPING  notes → libellé
// ══════════════════════════════════════════════════════════════════════════════

export const NOTES_LABELS = {
  chaud: '🔥 Très intéressé',
  moyen: '🤔 Intéressé moyen',
  froid: '❄️ Pas intéressé',
  perdu: '🚫 Perdu',  // ✅ AJOUTÉ
};

// ══════════════════════════════════════════════════════════════════════════════
//  CONVERTISSEUR  réponse Django → objet React
// ══════════════════════════════════════════════════════════════════════════════

export const fromApiRelance = (r) => ({
  id:                r.id,
  prospectId:        r.prospect,
  prospectNom:       r.prospect_nom       || '',
  prospectPrenom:    r.prospect_prenom    || '',
  prospectTelephone: r.prospect_telephone || '',
  dateRelance:       r.date_relance,          // 'YYYY-MM-DD'
  commentaire:       r.commentaire       || '',
  statut:            r.statut,                // slug persisté
  statutCalc:        r.statut_calcule,        // slug calculé (affiché)
  dateAction:        r.date_action       || null,
  createdBy:         r.created_by_nom    || '',
  dateCreation:      r.date_creation     || '',
  formationId:       r.formation         || null,
  formationNom: r.formations_nom || '',   // ← "Formation1, Formation2"
  formationsList: r.formations_list || [],
});

// ══════════════════════════════════════════════════════════════════════════════
//  APPELS API
// ══════════════════════════════════════════════════════════════════════════════

/** Toutes les relances — dashboard Home */
export const getAllRelances = async (params = {}) => {
  const res = await api.get(BASE, { params });
  return res.data.map(fromApiRelance);
};

/** Relances d'un prospect spécifique — page détail */
export const getProspectRelances = async (prospectId) => {
  const res = await api.get(PROSPECT(prospectId));
  return res.data.map(fromApiRelance);
};

/**
 * Créer une relance pour un prospect
 * 
 * @param {number} prospectId - ID du prospect
 * @param {object} data
 *   - dateRelance {string}     date au format YYYY-MM-DD
 *   - commentaire {string}     commentaire optionnel
 *   - formationIds {Array}     tableau de IDs de formation (optionnel)
 *                              Si vide, la première formation souhaitée sera assignée
 * 
 * Note : Le backend crée une relance PER formation.
 *        Si formationIds = [1, 2], deux relances seront créées.
 */
export const createRelance = async (prospectId, { dateRelance, commentaire, formationIds }) => {
  // Normalisation en tableau
  let normalizedIds = [];
  if (Array.isArray(formationIds)) {
    normalizedIds = formationIds.filter(id => id);
  } else if (formationIds) {
    normalizedIds = [formationIds];
  }

  const payload = {
    date_relance: dateRelance,
    commentaire: commentaire || '',
    formations: normalizedIds, // ← tableau d'IDs
  };

  const res = await api.post(PROSPECT(prospectId), payload);
  return fromApiRelance(res.data);
};

/** Modifier une relance (reprogrammation) */
export const updateRelance = async (id, { dateRelance, commentaire }) => {
  const res = await api.patch(`${BASE}${id}/`, {
    date_relance: dateRelance,
    commentaire:  commentaire || '',
  });
  return fromApiRelance(res.data);
};

/** Supprimer une relance */
export const deleteRelance = async (id) => {
  await api.delete(`${BASE}${id}/`);
};

/**
 * Action "OK" — appel effectué
 *
 * Le backend :
 *   1. Marque la relance statut='fait'
 *   2. Crée automatiquement un HistoriqueEchange :
 *      - source = 'relance'  (automatique, non affiché en UI)
 *      - type_echange, notes (niveau d'intérêt), contenu (résumé)
 *
 * @param {number} id
 * @param {object} payload
 *   - type_echange  {string}  'appel' | 'email' | 'message'
 *   - notes         {string}  'chaud' | 'moyen' | 'froid' | ''
 *   - contenu       {string}  résumé libre (optionnel)
 */
export const actionOk = async (id, payload = {}) => {
  const { type_echange = 'appel', notes = '', contenu = '' } = payload;
  const res = await api.post(`${BASE}${id}/ok/`, {
    type_echange,
    notes,
    contenu,
  });
  return {
    message: res.data.message,
    relance: fromApiRelance(res.data.relance),
  };
};

/** Compteur relances du jour (navbar badge) */
export const getRelancesCountToday = async () => {
  const res = await api.get('prospects/relances/count-today/');
  return res.data.count;
};